import { StatusConnError16, StatusConnecting16, StatusCritical16, StatusError16, StatusException16, StatusInfo16, StatusOff16, StatusOffline16, StatusOnline16, StatusSuccess16, StatusWarning16, } from '@yunex/yds-icons';
import { useLayoutEffect, useState } from 'react';
import { Theme } from '../themes';
export var statusResolverIconMap = {
    ok: StatusSuccess16,
    error: StatusError16,
    info: StatusInfo16,
    warning: StatusWarning16,
    unknown: StatusWarning16,
    critical: StatusCritical16,
    offline: StatusOffline16,
    off: StatusOff16,
    // Non pallete icons:
    online: StatusOnline16,
    exception: StatusException16,
    conn_error: StatusConnError16,
    connecting: StatusConnecting16,
};
/**
 * Key is from NonPaletteIcons and value is from palette.symbol
 */
var getColorForNonStatusIcon = function (status) {
    switch (status) {
        case 'online':
            return 'ok';
        case 'exception':
            return 'info';
        case 'conn_error':
            return 'critical';
        case 'connecting':
            return 'offline';
        default:
            return status;
    }
};
/**
 * Function to transform the specified string into a normalized key.
 * Example: " List item " -> "list-item"
 *
 * @param input The input string to normalize.
 * @returns The normalized key.
 */
export var normalizeKey = function (input) {
    return (input !== null && input !== void 0 ? input : '').trim().toLowerCase().replaceAll(' ', '-');
};
/**
 * Checks if mode of the specified `theme` is `light` or not.
 *
 * @param theme The current theme.
 * @returns `true` if the mode is light, otherwise `false`.
 */
export var isLightTheme = function (theme) { var _a; return ((_a = theme === null || theme === void 0 ? void 0 : theme.palette) === null || _a === void 0 ? void 0 : _a.mode) === Theme.Light; };
/**
 * Wraps the specified `sx` props into array.
 * Example usage: sx={[stylesToMerge, ...resolveSxAsArray(incomingSx)]}
 *
 * @param sx The sx props to wrap.
 * @returns Either the specified sx props as an array or an empty array in case no value provided.
 */
export var resolveSxAsArray = function (sx) {
    if (!sx) {
        return [];
    }
    return Array.isArray(sx) ? sx : [sx];
};
/**
 * Returns an icon component based on the specified status. If status is not one of the known statuses, returns `StatusWarning16`.
 * @default StatusWarning16
 *
 * @param status The string defined in `IStatusResolverIconMap`.
 */
export var resolveStatusIcon = function (status) {
    return statusResolverIconMap[status] || statusResolverIconMap.warning;
};
/**
 * Returns a color from a palette based on the specified status. If status is not one of the known statuses, returns color of unknown.
 *
 * @param status The string defined in `IStatusResolverIconMap`.
 */
export var resolveStatusColor = function (status, palette) {
    return palette.symbol[getColorForNonStatusIcon(status)] || palette.symbol.unknown;
};
// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
export function formatBytes(bytes, decimals) {
    if (decimals === void 0) { decimals = 2; }
    if (!+bytes)
        return '0 Bytes';
    var k = 1024;
    var dm = decimals < 0 ? 0 : decimals;
    var sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    var i = Math.floor(Math.log(bytes) / Math.log(k));
    return "".concat(parseFloat((bytes / Math.pow(k, i)).toFixed(dm)), " ").concat(sizes[i]);
}
/**
 * Helper function to remove duplicates from an array of objects.
 *
 * @param arr Array of objects.
 * @param key Key to be used for filtering.
 * @returns Array of objects with unique values for the specified key.
 */
export var removeDuplicatesByKey = function (arr, key) {
    var seen = new Set();
    return arr.filter(function (el) {
        var duplicate = seen.has(el[key]);
        seen.add(el[key]);
        return !duplicate;
    });
};
/**
 * If text has ellipsis (...) or is longer than 21 chars, return true.
 * We are calculating characters because comparing only clientWidth and scrollWidth does not work in edge cases.
 */
function isEllipsisActive(el, charCount) {
    var isOverflowing = el.clientWidth < el.scrollWidth;
    if (charCount) {
        var isTruncated = el.textContent ? el.textContent.length > charCount : false;
        return isOverflowing || isTruncated;
    }
    return isOverflowing;
}
/**
 * Check if element or its children has ellipsis or is longer than charCount chars.
 */
export var useIsOverflowWidth = function (ref, options) {
    var _a = useState(), isOverflow = _a[0], setIsOverflow = _a[1];
    var _b = options || {}, charCount = _b.charCount, childIndex = _b.childIndex, callback = _b.callback;
    useLayoutEffect(function () {
        var current = ref.current;
        var trigger = function () {
            var hasOverflow = false;
            if (childIndex !== undefined && current.children.length > childIndex) {
                hasOverflow = isEllipsisActive(current.children[childIndex], charCount);
            }
            else {
                hasOverflow = isEllipsisActive(current, charCount);
            }
            setIsOverflow(hasOverflow);
            callback === null || callback === void 0 ? void 0 : callback(hasOverflow);
        };
        if (current) {
            trigger();
        }
    }, [callback, ref, charCount]);
    return isOverflow;
};
export { rem, em } from './rem';
export { default as composeRefs } from './composeRefs';
